import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckCircle,
  Close,
  Error,
  FolderOpen,
  KeyboardArrowDown,
  KeyboardArrowUp,
  VBasePopover,
  VFlex,
  VLoadingSpinner,
  VStack,
  VVirtualScroll,
  cn,
} from '@/package';
import { ExtIcon } from '@/package/kiska/src/component/UI/VFileBrowser/constant';
import {
  getExt,
  isImage,
  isPcd,
} from '@/package/kiska/src/component/UI/VFileBrowser/util';

type FileItem = {
  error?: Nullish<Error>;
  name: string;
  status: 'pending' | 'canceled' | 'error' | 'done' | 'progress';
  isDir?: boolean;
};

type CollectionFileProgressProps<T> = {
  enableCancelUpload?: boolean;
  itemList: T[];
  type: 'download' | 'upload';
  onCloseClick: () => void;
  onItemClick?: (item: FileItem) => void;
  onUploadCancel?: () => void;
};

const CollectionFileProgress = <T extends FileItem>({
  enableCancelUpload,
  itemList,
  type,
  onCloseClick,
  onItemClick,
  onUploadCancel,
}: CollectionFileProgressProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const totalItemNum = itemList.length;
  const completedItemNum = itemList.filter(
    item => item.status === 'done',
  ).length;
  const ArrowIcon = isExpanded ? KeyboardArrowDown : KeyboardArrowUp;
  const isUploadType = type === 'upload';

  return (
    <VStack className="w-[354px] gap-1 rounded-md border border-gray-200 bg-white px-4 py-2 shadow-sm">
      <VFlex className="w-full justify-between">
        <div className="text-body-xl font-semibold">
          {isUploadType ? 'Upload' : 'Download'} {completedItemNum} of{' '}
          {totalItemNum} items
        </div>
        <VFlex gap={0.5} items="center">
          <ArrowIcon
            className="h-6 w-6 text-black"
            role="button"
            onClick={() => setIsExpanded(prev => !prev)}
          />
          <Close
            className="h-6 w-6 text-gray-400"
            role="button"
            onClick={onCloseClick}
          />
        </VFlex>
      </VFlex>

      {isUploadType && enableCancelUpload && (
        <VFlex
          className="mb-1 rounded-md bg-skyBlue-200 px-2 py-1 text-body-md"
          justify="between"
          items="center"
        >
          <div>Uploading...</div>
          <div role="button" className="text-gray-500" onClick={onUploadCancel}>
            <strong>cancel</strong>
          </div>
        </VFlex>
      )}

      {isExpanded && (
        <div className="h-[160px] max-h-[160px] w-full">
          <VVirtualScroll
            className="overflow-x-hidden"
            item={{
              list: itemList,
              height: 32,
            }}
            renderItem={item => <Item item={item} onItemClick={onItemClick} />}
          />
        </div>
      )}
    </VStack>
  );
};

const Item = ({
  item,
  onItemClick,
}: {
  item: FileItem;
  onItemClick?: (item: FileItem) => void;
}) => {
  const [isHoverd, setIsHoverd] = useState(false);
  const itemClickable = isHoverd && item.status === 'done' && !!onItemClick;

  return (
    <VFlex
      items="center"
      gap={0.25}
      className={cn('py-1', {
        'cursor-pointer hover:relative hover:after:absolute hover:after:top-0 hover:after:left-1 hover:after:z-[1] hover:after:h-full hover:after:w-full hover:after:shadow-sm hover:after:content-[""]':
          itemClickable,
      })}
      onClick={() => itemClickable && onItemClick?.(item)}
      onMouseEnter={() => setIsHoverd(true)}
      onMouseLeave={() => setIsHoverd(false)}
    >
      <ExtensionIcon item={item} />
      <div className="flex-1 overflow-x-auto text-body-xl">{item.name}</div>
      {itemClickable ? (
        <FolderOpen className="h-5 w-5 fill-black text-white" />
      ) : (
        <StatusIcon item={item} />
      )}
    </VFlex>
  );
};

const ExtensionIcon = ({ item }: { item: FileItem }) => {
  const ext = getExt(item.name);
  const isDir = item.isDir;

  return !ext || isDir ? (
    <ExtIcon.Folder />
  ) : isImage(ext) ? (
    <ExtIcon.Image />
  ) : isPcd(ext) ? (
    <ExtIcon.Pcd />
  ) : (
    <ExtIcon.File />
  );
};

const StatusIcon = ({ item }: { item: FileItem }) => {
  const { t } = useTranslation();

  if (item.status === 'progress' || item.status === 'pending') {
    return <VLoadingSpinner className="!h-4 !w-4" />;
  }

  if (item.status === 'canceled') {
    return (
      <div className="py-1 text-gray-400">
        {t('collection.cancelUpload.katakana')}
      </div>
    );
  }

  if (item.status === 'done') {
    return <CheckCircle className="size-4 text-success-500" />;
  }

  if (item.status === 'error') {
    return (
      <VBasePopover
        content={() => (
          <div className="px-1 py-0.75">
            {item.error?.message ?? 'Upload Error'}
          </div>
        )}
        placement="left"
      >
        <span
          role="img"
          className='relative after:absolute after:top-0 after:left-0 after:z-[1] after:size-full after:content-[""]'
        >
          <Error className="size-4 text-error-500" />
        </span>
      </VBasePopover>
    );
  }
};

export default CollectionFileProgress;
